import { DndContext, DragOverlay, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToWindowEdges } from '@dnd-kit/modifiers'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { EyeIcon, HammerIcon, ArrowLeftIcon } from 'lucide-react'
import { useEffect, useState, useContext } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import axiosInstance from '../../api/axiosInstance'
import { FormElementButton } from '../../components/create-form/DraggableButton'
import FormElements from '../../components/create-form/FormElements'
import FormPlayground from '../../components/create-form/FormPlayground'
import FormPreview from '../../components/create-form/FormPreview'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Switch } from '../../components/ui/switch'
import { AuthContext } from '../../contexts/auth-context'
import { KeyboardSensor, PointerSensor } from '../../lib/dndKitSensors'
import { useFormPlaygroundStore } from '../../stores/formPlaygroundStore'

function CreateForm({ formType = 'add', form }) {
  const { auth } = useContext(AuthContext)
  const { id } = useParams()
  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const formElements = useFormPlaygroundStore((state) => state.formElements)
  const addFormElement = useFormPlaygroundStore((state) => state.addFormElement)
  const removeAllFormElements = useFormPlaygroundStore((state) => state.removeAllFormElements)

  const [activeButton, setActiveButton] = useState(null)
  const [isPreview, setIsPreview] = useState(false)
  const [formName, setFormName] = useState(form?.name ?? '')
  const [formLogo, setFormLogo] = useState(null)
  const [logoPreview, setLogoPreview] = useState(null)
  const [isDropped, setIsDropped] = useState(false)

  useEffect(() => {
    if (form?.logo) setLogoPreview(form.logo)
  }, [form])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const { mutate } = useMutation({
    mutationFn: async (formData) => {
      const data = new FormData()
      data.append('businessId', formData.businessId)
      data.append('name', formData.name)
      data.append('elements', JSON.stringify(formData.elements))
      data.append('isActive', false)
      if (formData.logoUrl) data.append('logo', formData.logoUrl)

      return axiosInstance({
        url: formType === 'add' ? '/addForm' : '/forms/' + id,
        method: formType === 'add' ? 'post' : 'patch',
        data,
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    },
    onSuccess: () => {
      if (formType === 'edit') navigate('/my-forms')
      queryClient.invalidateQueries({ queryKey: ['forms'] })
      setFormName('')
      setFormLogo(null)
      setLogoPreview(null)
      removeAllFormElements()
      toast.success(`Form ${formType === 'add' ? 'created' : 'updated'} successfully`)
      navigate('/business/my-forms')
    },
    onError: () => toast.error(`Error ${formType === 'add' ? 'creating' : 'updating'} form`),
  })

  return (
    <DndContext
      sensors={sensors}
      onDragStart={(e) => {
        setActiveButton(e.active.data.current?.element)
        setIsDropped(false)
      }}
      onDragCancel={() => {
        setActiveButton(null)
        setIsDropped(false)
      }}
      onDragEnd={({ over, active }) => {
        setActiveButton(null)
        if (!over) return
        addFormElement(active.data.current?.element.text, active.id)
        setIsDropped(true)
      }}
    >
      {/* Back button - now visible on all screen sizes */}
      {/* Moved outside the main flex container to ensure it's always at the top of the content area */}
      <div className="px-4 pt-4">
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 mb-4 cursor-pointer"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back
        </Button>
      </div>

      {/* Main container changed to flex-col on mobile, flex-row on larger screens */}
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-12 px-4 py-4">
        {/* Form Elements Sidebar - Adjusted width for mobile */}
        <div className="w-full lg:w-1/3">
          <FormElements isUpdate={formType === 'edit'} />
        </div>

        {/* Main Form Section - Takes full width on mobile */}
        <form
          className="w-full flex flex-col"
          onSubmit={(e) => {
            e.preventDefault()
            if (!formName.trim()) return toast.error('Form name is required')
            if (formType === 'add' && !formLogo) return toast.error('Form logo is required')
            if (formElements.length === 0) return toast.error('Form is empty!')

            const requiredFields = [
              { type: 'mobile-number', label: 'Mobile Number' },
              { type: 'name', label: 'Name' },
              { type: 'email', label: 'Email' },
            ]

            const optionalFields = [
              { type: 'heading', label: 'Heading' },
              { type: 'description', label: 'Description' },
            ]

            const fieldCounts = {}
            ;[...requiredFields, ...optionalFields].forEach(({ type, label }) => {
              const key = `${type}:${label}`
              fieldCounts[key] = 0
            })

            function stripHTML(str) {
              return str.replace(/<[^>]*>?/gm, '').trim()
            }

            for (const element of formElements) {
              const cleanedLabel = stripHTML(element.label || '')
              for (const { type, label } of [...requiredFields, ...optionalFields]) {
                if (element.type === type && cleanedLabel === label) {
                  const key = `${type}:${label}`
                  fieldCounts[key]++
                }
              }
            }

            for (const { type, label } of requiredFields) {
              const key = `${type}:${label}`
              if (fieldCounts[key] === 0)
                return toast.error(`Form must include one "${label}" field.`)
              else if (fieldCounts[key] > 1)
                return toast.error(`Only one "${label}" field is allowed.`)
            }

            for (const { type, label } of optionalFields) {
              const key = `${type}:${label}`
              if (fieldCounts[key] > 1) return toast.error(`Only one "${label}" field is allowed.`)
            }

            mutate({
              businessId: auth?.user?._id,
              name: formName,
              elements: formElements,
              logoUrl: formLogo,
              isActive: false,
            })
          }}
        >
          {/* Header Section - Adjusted for stacked layout on mobile */}
          <section className="mb-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col gap-4 w-full md:w-auto">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <label className="font-medium">Form Name:</label>
                <Input
                  placeholder="Enter form name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full sm:w-64"
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <label className="font-medium">Form Logo:</label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0]
                    if (file) {
                      setFormLogo(file)
                      setLogoPreview(URL.createObjectURL(file))
                    }
                  }}
                  className="w-full sm:w-64"
                />
                {logoPreview && (
                  <img
                    src={logoPreview}
                    alt="Form Logo"
                    className="h-10 w-10 object-cover rounded mt-2 sm:mt-0"
                  />
                )}
              </div>
            </div>

            {/* Builder/Preview Toggle - Flex layout adjusted for mobile */}
            <div className="flex items-center justify-between md:justify-start gap-3 text-sm font-medium">
              <div
                className={`flex items-center gap-2 ${!isPreview ? 'text-theme-color-600' : ''}`}
              >
                <HammerIcon className="h-5 w-5" />
                <span>Builder</span>
              </div>
              <Switch
                className="data-[state=unchecked]:bg-theme-color-700"
                checked={isPreview}
                onCheckedChange={setIsPreview}
              />
              <div className={`flex items-center gap-2 ${isPreview ? 'text-theme-color-600' : ''}`}>
                <EyeIcon className="h-5 w-5" />
                <span>Preview</span>
              </div>
            </div>
          </section>

          {/* Builder or Preview Area */}
          <div className="w-full min-h-[300px]">
            {isPreview ? (
              <FormPreview />
            ) : (
              <FormPlayground
                isDropped={isDropped}
                resetIsDropped={() => setIsDropped(false)}
                isUpdate={formType === 'edit'}
              />
            )}
          </div>

          {/* Footer Buttons - Adjusted for full width on mobile, then auto */}
          <section className="mt-6 flex flex-wrap justify-end gap-4">
            {form && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" variant="outline" className="w-full sm:w-auto">
                    Cancel
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure you want to cancel?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Any unsaved changes will be lost.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter className="flex flex-col sm:flex-row sm:space-x-4 gap-2 sm:gap-0">
                    <AlertDialogAction onClick={() => navigate('/my-forms')}>
                      Yes, exit
                    </AlertDialogAction>
                    <AlertDialogCancel>Keep editing</AlertDialogCancel>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}

            {formElements.length !== 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" variant="destructive" className="w-full sm:w-auto">
                    Clear Form
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Clear Form?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to clear the form? This will remove all progress.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter className="flex flex-col sm:flex-row sm:space-x-4 gap-2 sm:gap-0">
                    <AlertDialogAction onClick={removeAllFormElements}>
                      Yes, clear form
                    </AlertDialogAction>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}

            <Button className="bg-theme-color-800 w-full sm:w-auto hover:text-theme-color-800 hover:bg-white hover:border hover:border-theme-color-700">
              <span>{form ? 'Update Form' : 'Save Form'}</span>
            </Button>
          </section>
        </form>
      </div>

      <DragOverlay modifiers={[restrictToWindowEdges]}>
        {activeButton ? <FormElementButton className="cursor-grabbing" {...activeButton} /> : null}
      </DragOverlay>
    </DndContext>
  )
}

export default CreateForm
